import { DEFAULT_ECOPACK_IMAGE_URL } from '@/constants';

interface Product {
  id: string;
  name: string;
  description?: string;
  original_price: number;
  discounted_price: number;
  discount_rate: number;
  discount_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  stock_quantity: number;
  is_available: boolean;
  image_url?: string;
  deadlineOfferAvailableToday?: boolean;
}

interface DesktopProductCardProps {
  product: Product;
  isHiding: boolean;
  readonly: boolean;
  onClick: () => void;
}

export function DesktopProductCard({ product, isHiding, readonly, onClick }: DesktopProductCardProps) {
  const isOutOfStock = product.discount_type === 'RESERVATION_DISCOUNT' 
    ? false 
    : (product.stock_quantity === 0 || !product.is_available);
  const isDeadlineInactive = product.discount_type === 'DEADLINE_DISCOUNT' && product.deadlineOfferAvailableToday === false;
  const isDisabled = isOutOfStock || isDeadlineInactive;
  const discountRateFromPrice = product.original_price > 0
    ? Math.round(((product.original_price - product.discounted_price) / product.original_price) * 100)
    : 0;
  const baseDiscountRate = product.discount_rate > 0
    ? product.discount_rate
    : Math.max(discountRateFromPrice, 0);
  const displayDiscountRate = product.discount_type === 'DEADLINE_DISCOUNT'
    ? (baseDiscountRate > 0 ? baseDiscountRate : 40)
    : baseDiscountRate;
  const derivedOriginalPrice = product.discount_type === 'DEADLINE_DISCOUNT'
    ? (() => {
        if (product.original_price > product.discounted_price) return product.original_price;
        const rate = displayDiscountRate || 40;
        const estimated = product.discounted_price / (1 - rate / 100);
        return Math.ceil(estimated / 10) * 10; // 1원 단위 올림하여 10원 단위로 표시
      })()
    : product.original_price;
  const hasDiscount = product.discount_type === 'DEADLINE_DISCOUNT'
    ? true
    : (product.discounted_price < product.original_price && displayDiscountRate > 0);
  const productImageUrl = product.image_url || (product.discount_type === 'DEADLINE_DISCOUNT' ? DEFAULT_ECOPACK_IMAGE_URL : undefined);

  return (
    <div
      onClick={readonly ? undefined : onClick}
      className={`
        relative bg-white border-2 border-[#DEDEDE] rounded-2xl p-5 min-h-[320px] h-auto flex flex-col w-full transition-all duration-500 ease-out
        ${isHiding 
          ? 'opacity-0 scale-95 -translate-y-2' 
          : 'opacity-100 scale-100 translate-y-0'
        }
        ${isOutOfStock || isDeadlineInactive
          ? 'grayscale opacity-50' 
          : ''
        }
        ${readonly ? 'cursor-default' : 'cursor-pointer'}
        ${!readonly && !isDisabled ? ' hover:shadow-lg hover:border-[#86DB89]' : ''}
      `}
    >
      {/* 품절 오버레이 */}
      {isOutOfStock && (
        <div className="pointer-events-none absolute inset-0 bg-black/60 rounded-2xl flex items-center justify-center z-10">
          <span className="text-white text-2xl font-bold">품절</span>
        </div>
      )}
      {/* 마감할인 비활성 오버레이 */}
      {isDeadlineInactive && !isOutOfStock && (
        <div className="pointer-events-none absolute inset-0 bg-black/20 rounded-2xl z-10" />
      )}
      
      {/* 상품 이미지 */}
      {productImageUrl && (
        <div className="w-full h-36 mb-3 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
          <img
            src={productImageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      {/* 컨텐츠 영역 - flex-grow로 공간 확보 */}
      <div className="flex-grow flex flex-col">
        {/* 제품명 */}
        <h4 className="text-lg font-bold text-[#222222] mb-2 line-clamp-2">{product.name}</h4>
        
        {/* 설명 */}
        {product.description && (
          <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-2 flex-grow">
            {product.description}
          </p>
        )}
        
        {/* 재고 정보 */}
        <div className="flex items-center justify-between mb-3">
          {product.discount_type === 'RESERVATION_DISCOUNT' ? (
            <div className="flex items-center gap-1 border-b border-[#222222] pb-0.5">
              <span className="text-sm font-medium text-green-600">예약 가능</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 border-b border-[#222222] pb-0.5">
              <span className="text-sm text-[#222222]">재고</span>
              <span className={`text-sm font-bold ${
                product.stock_quantity <= 1 ? 'text-[#FF3B30]' : 'text-[#222222]'
              }`}>
                {product.stock_quantity}
              </span>
              <span className="text-sm text-[#222222]">개</span>
            </div>
          )}
        </div>
      </div>
      
      {/* 가격 정보 - 한 줄로 표시 */}
      <div className="mt-auto">
        {hasDiscount ? (
          <div className="flex items-center justify-end gap-1">
            <span className="text-sm text-gray-500 line-through">
              {derivedOriginalPrice.toLocaleString()}원
            </span>
            <img 
              src="/icons/icon_arrow_right.svg" 
              alt="할인 화살표" 
              className="w-3 h-3"
            />
            <span className="text-sm font-medium text-[#FF3B30]">
              (-{displayDiscountRate}%)
            </span>
            <span className="text-lg font-bold text-[#222222]">
              {product.discounted_price.toLocaleString()}원
            </span>
          </div>
        ) : (
          <div className="text-right">
            <span className="text-lg font-bold text-[#222222]">
              {product.discounted_price.toLocaleString()}원
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
